import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, ViewChild } from '@angular/core';

import { MarketSubType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TooltipModule } from '@frontend/sports/tooltip-feature';
import { NOT_APPLICABLE, TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { PopperContentComponent } from '@frontend/vanilla/features/popper';
import { NgxFloatUiModule, NgxFloatUiPlacements } from 'ngx-float-ui';

import { MarketSubTypeLogoService } from './marketsubtype-logo.service';
import { MarketSubTypeTooltipService } from './marketsubtype-tooltip.service';

@Component({
    selector: 'ms-marketsubtype-logo',
    templateUrl: './marketsubtype-logo.component.html',
    styleUrls: ['./marketsubtype-logo.component.scss'],
    imports: [TooltipModule, NgxFloatUiModule, PopperContentComponent, CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketSubtypeLogoComponent implements OnChanges {
    @Input() marketType?: MarketType;
    @Input() marketSubType?: MarketSubType;
    @Input() matchName: string;
    @Input() sportId?: SportConstant;
    @Input() templateId?: string;
    @Input() eventId?: string;
    @Input() tooltipAppendTo: string;
    @Input() tooltipBoundaries: string;
    @Input() tooltipPlacement: NgxFloatUiPlacements;
    @Input() trackingLocation: string;
    @Input() wrapMarketLogoTitle?: boolean;
    @Input() chevronShown = false;
    @Input() signpostingText?: string;
    @ViewChild('popper') marketSubtypePopper: PopperContentComponent;
    @HostBinding('class') className = 'marketsubtype-logo-var-scope';

    tooltip = {
        showTooltip: false,
        title: '',
        message: '',
        linkText: '',
    };

    logoTitle?: string;

    constructor(
        public sitecore: Sitecore,
        private tooltipService: MarketSubTypeTooltipService,
        private logoService: MarketSubTypeLogoService,
        private trackingService: TrackingService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<MarketSubtypeLogoComponent>): void {
        if (
            changes.signpostingText ||
            ((changes.marketSubType || changes.marketType || changes.sportId) &&
                this.sportId &&
                ((this.marketType && this.marketSubType) || this.templateId))
        ) {
            const sitecorePrefix =
                this.marketType && this.marketSubType
                    ? this.logoService.getMarketSubTypeLogoSitecorePrefix(this.sportId, this.marketType, this.marketSubType)
                    : this.logoService.getTemplateIdSitecorePrefix(this.sportId, this.templateId);
            this.logoTitle =
                this.signpostingText ??
                this.logoService.getMarketSubTypeLogoTitle(this.eventId!, this.sportId, this.marketType, this.marketSubType, this.templateId);

            this.tooltip = {
                showTooltip: false,
                title: this.sitecore.marketSubtypeLogo.messages[`${sitecorePrefix}-TooltipTitle`],
                message: this.sitecore.marketSubtypeLogo.messages[`${sitecorePrefix}-TooltipText`],
                linkText: this.sitecore.marketSubtypeLogo.messages[`${sitecorePrefix}-TooltipLink`],
            };
        }
    }

    toggleTooltip(event: MouseEvent): void {
        event.stopPropagation();
        this.trackBadgeClick();

        if (!this.tooltip.showTooltip) {
            this.tooltipService.tooltip = this.marketSubtypePopper;
        }

        this.tooltip.showTooltip = !this.tooltip.showTooltip;
    }

    onTooltipLinkClick(): void {
        const tooltipLinkTitle = this.tooltip.linkText?.substring(this.tooltip.linkText.indexOf('>') + 1, this.tooltip.linkText.lastIndexOf('<'));

        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: tooltipLinkTitle,
            [trackingConstants.COMPONENT_URL_CLICKED]: this.getTooltipUrl(),
        });
    }

    onCloseTooltip(): void {
        this.tooltip.showTooltip = false;
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'close',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: '2up popup',
        });
    }

    trackPopupShow(): void {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: '2up popup',
        });
    }

    private getTrackingObject = (): Partial<TrackData> => {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betting',
            [trackingConstants.COMPONENT_LABEL_EVENT]: '2up early payout',
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.matchName,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.trackingLocation,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };
    };

    private trackBadgeClick(): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.logoTitle,
        });
    }

    private getTooltipUrl(): string {
        const regex = /href="([^"]*)"/;

        const tooltipUrl = this.tooltip.linkText.match(regex);
        if (tooltipUrl && tooltipUrl.length >= 2) {
            return tooltipUrl[1];
        }

        return NOT_APPLICABLE;
    }

    get popperClass(): string {
        return `marketsubtype-logo-var-scope ${this.chevronShown ? ' market-subtype-tooltip-center' : ''}`;
    }
}
